import React, {
 useState
} from 'react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 ArrowLeft, Save, Plus, GitBranch, Loader2
} from 'lucide-react';
import {
    WorkflowListItem, WorkflowStep, WorkflowStepType,
    FetchStepConfig, ApiCallStepConfig, MutateStepConfig,
} from '../types/workflow.types';
import WorkflowStepCard from './workflow-editor/WorkflowStepCard';

interface WorkflowEditorProps {
    workflow: WorkflowListItem | null;
    prefillData?: { name: string; description: string; steps: WorkflowStep[] } | null;
    onSave: (data: { name: string; description: string; steps: WorkflowStep[] }) => void | Promise<void>;
    onCancel: () => void;
}

let stepIdCounter = 0;
function nextStepId(): string {
    stepIdCounter += 1;
    return `step_${Date.now()}_${stepIdCounter}`;
}

const defaultStepConfigs: Record<WorkflowStepType, FetchStepConfig | ApiCallStepConfig | MutateStepConfig> = {
    FETCH: {
        schemaName: '',
        fields: [],
        filters: [],
        limit: 100,
        orderBy: '',
        orderDirection: 'asc',
    },
    API_CALL: {
        connectionId: '',
        method: 'GET',
        path: '',
        headers: {},
        body: '',
    },
    MUTATE: {
        operation: 'INSERT',
        schemaName: '',
        fieldMappings: [],
        condition: '',
    },
};

function createNewStep(): WorkflowStep {
    return {
        id: nextStepId(),
        type: 'FETCH',
        name: '',
        config: {
 ...defaultStepConfigs.FETCH
} as FetchStepConfig,
    };
}

const WorkflowEditor: React.FC<WorkflowEditorProps> = ({
    workflow, prefillData, onSave, onCancel,
}) => {
    const [name, setName] = useState(workflow?.name || prefillData?.name || '');
    const [description, setDescription] = useState(workflow?.description || prefillData?.description || '');
    const [steps, setSteps] = useState<WorkflowStep[]>(
        workflow?.steps || prefillData?.steps || []
    );
    const [expandedStepId, setExpandedStepId] = useState<string | null>(
        steps.length > 0 ? steps[0].id : null
    );
    const [isSaving, setIsSaving] = useState(false);

    const isEditing = !!workflow;

    const addStep = (afterIndex: number) => {
        const newStep = createNewStep();
        const newSteps = [...steps];
        newSteps.splice(afterIndex + 1, 0, newStep);
        setSteps(newSteps);
        setExpandedStepId(newStep.id);
    };

    const removeStep = (stepId: string) => {
        setSteps((prev) => prev.filter((s) => s.id !== stepId));
        if (expandedStepId === stepId) {
            setExpandedStepId(null);
        }
    };

    const updateStep = (stepId: string, data: Partial<WorkflowStep>) => {
        setSteps((prev) =>
            prev.map((s) => (s.id === stepId ? {
 ...s,
...data
} : s))
        );
    };

    const updateStepConfig = (stepId: string, config: FetchStepConfig | ApiCallStepConfig | MutateStepConfig) => {
        setSteps((prev) =>
            prev.map((s) => (s.id === stepId ? {
 ...s,
config
} : s))
        );
    };

    const handleSave = async () => {
        if (!name.trim()) return;
        setIsSaving(true);
        try {
            await onSave({
 name,
description,
steps
});
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-4 w-full py-4">
            {/* Header */}
            <div>
                <Button variant="ghost" size="sm" onClick={onCancel} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Workflows
                </Button>
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold">
                        {isEditing ? 'Edit Workflow' : 'Create New Workflow'}
                    </h2>
                    <Button onClick={handleSave} disabled={!name.trim() || isSaving}>
                        {isSaving ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="mr-2 h-4 w-4" />
                        )}
                        {isSaving ? 'Saving...' : 'Save Workflow'}
                    </Button>
                </div>
            </div>

            {/* Name & Description */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                <div className="space-y-1.5">
                    <Label>Workflow Name</Label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Sync Users to CRM"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label>Description</Label>
                    <Textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="What does this workflow do?"
                        rows={1}
                        className="resize-none min-h-[40px]"
                    />
                </div>
            </div>

            {/* Step Canvas */}
            <div className="mt-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Steps</h3>
                    <span className="text-sm text-muted-foreground">
                        {steps.length} step{steps.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {steps.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3 border rounded-md border-dashed">
                        <GitBranch className="h-10 w-10 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">No steps yet. Add your first step to get started.</p>
                        <Button size="sm" onClick={() => addStep(-1)}>
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            Add First Step
                        </Button>
                    </div>
                ) : (
                    <div className="max-w-3xl">
                        {steps.map((step, idx) => (
                            <WorkflowStepCard
                                key={step.id}
                                step={step}
                                stepIndex={idx}
                                isExpanded={expandedStepId === step.id}
                                isLast={idx === steps.length - 1}
                                allSteps={steps}
                                onToggleExpand={() => setExpandedStepId(
                                    expandedStepId === step.id ? null : step.id
                                )}
                                onUpdate={(data) => updateStep(step.id, data)}
                                onUpdateConfig={(config) => updateStepConfig(step.id, config)}
                                onDelete={() => removeStep(step.id)}
                                onAddAfter={() => addStep(idx)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WorkflowEditor;
